import type { ApiClient } from "@/api/client";
import { API_VERSION_PREFIX, newIdempotencyKey } from "@/api/client";
import type {
  AccessAssignmentDto,
  AccessScopeDto,
  AdminListResponse,
  AuditLogDto,
  ConfigurationAction,
  ConfigurationDto,
  EmployeeAccessAction,
  FeatureFlagAction,
  FeatureFlagDto,
  IntegrationEventDto,
  ListParams,
  MaintenanceDto,
  MaintenanceModeAction,
  PermissionGrantDto,
  RequestMeta,
} from "./types";

const BASE = `${API_VERSION_PREFIX}/hq/system`;

function toQuery(params: ListParams = {}): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === "") continue;
    search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

export function createSystemApi(client: ApiClient) {
  return {
    listConfigurations(params: ListParams = {}) {
      return client.get<AdminListResponse<ConfigurationDto>>(
        `${BASE}/configurations${toQuery(params)}`,
      );
    },
    getConfiguration(id: string) {
      return client.get<{ configuration: ConfigurationDto; meta: RequestMeta }>(
        `${BASE}/configurations/${id}`,
      );
    },
    applyConfigurationAction(
      id: string,
      action: ConfigurationAction,
      body: Record<string, unknown>,
    ) {
      return client.post(
        `${BASE}/configurations/${id}/actions/${action}`,
        body,
        newIdempotencyKey(),
      );
    },

    listFeatureFlags(params: ListParams = {}) {
      return client.get<AdminListResponse<FeatureFlagDto>>(
        `${BASE}/feature-flags${toQuery(params)}`,
      );
    },
    getFeatureFlag(id: string) {
      return client.get<{ feature_flag: FeatureFlagDto; meta: RequestMeta }>(
        `${BASE}/feature-flags/${id}`,
      );
    },
    applyFeatureFlagAction(
      id: string,
      action: FeatureFlagAction,
      body: Record<string, unknown>,
    ) {
      return client.post(
        `${BASE}/feature-flags/${id}/actions/${action}`,
        body,
        newIdempotencyKey(),
      );
    },

    listMaintenance(params: ListParams = {}) {
      return client.get<AdminListResponse<MaintenanceDto>>(
        `${BASE}/maintenance-modes${toQuery(params)}`,
      );
    },
    getMaintenance(id: string) {
      return client.get<{ maintenance_mode: MaintenanceDto; meta: RequestMeta }>(
        `${BASE}/maintenance-modes/${id}`,
      );
    },
    applyMaintenanceAction(
      id: string,
      action: MaintenanceModeAction,
      body: Record<string, unknown>,
    ) {
      return client.post(
        `${BASE}/maintenance-modes/${id}/actions/${action}`,
        body,
        newIdempotencyKey(),
      );
    },

    listAccessAssignments(params: ListParams = {}) {
      return client.get<AdminListResponse<AccessAssignmentDto>>(
        `${BASE}/access-assignments${toQuery(params)}`,
      );
    },
    getAccessAssignment(id: string) {
      return client.get<{
        assignment: AccessAssignmentDto;
        meta: RequestMeta;
      }>(`${BASE}/access-assignments/${id}`);
    },
    listAccessScopes(assignmentId: string) {
      return client.get<{ scopes: AccessScopeDto[]; meta: RequestMeta }>(
        `${BASE}/access-assignments/${assignmentId}/scopes`,
      );
    },
    listPermissionGrants(assignmentId: string) {
      return client.get<{ grants: PermissionGrantDto[]; meta: RequestMeta }>(
        `${BASE}/access-assignments/${assignmentId}/grants`,
      );
    },
    applyAccessAssignmentAction(
      id: string,
      action: EmployeeAccessAction,
      body: Record<string, unknown>,
    ) {
      return client.post(
        `${BASE}/access-assignments/${id}/actions/${action}`,
        body,
        newIdempotencyKey(),
      );
    },

    listAuditLogs(params: ListParams = {}) {
      return client.get<AdminListResponse<AuditLogDto>>(
        `${BASE}/audit-logs${toQuery(params)}`,
      );
    },
    getAuditLog(id: string) {
      return client.get<{ audit_log: AuditLogDto; meta: RequestMeta }>(
        `${BASE}/audit-logs/${id}`,
      );
    },

    listIntegrationEvents(params: ListParams = {}) {
      return client.get<AdminListResponse<IntegrationEventDto>>(
        `${BASE}/integration-events${toQuery(params)}`,
      );
    },
    getIntegrationEvent(id: string) {
      return client.get<{
        integration_event: IntegrationEventDto;
        meta: RequestMeta;
      }>(`${BASE}/integration-events/${id}`);
    },
  };
}

export type SystemApi = ReturnType<typeof createSystemApi>;
